import { useState, useEffect } from 'react';
import { Globe, FileText, Loader2 } from 'lucide-react';
import { useTrackEvent } from '@ideoz/core/analytics';
import {
  toastSuccess,
  showToast,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
  Icon,
} from '@ideoz/ui';

export interface PrototyperPublishMenuProps {
  onPublish?: (slug: string | null, options?: { republish?: boolean }) => void | Promise<void>;
  prototyperDemoLinkId?: number;
  prototyperSlug?: string | null;
  userName?: string;
  platformLink?: string;
  /** True once the WebContainer path has produced a build that's actually publishable. */
  publishReady?: boolean;
  /**
   * Reason Publish/Update can't run right now (e.g. "Build in progress…", "Rebuild to
   * publish"). When set, the control stays visible but renders disabled with this as its
   * tooltip — never hidden, so the user always knows the feature exists and why it's
   * momentarily unavailable. `null`/`undefined` means ready.
   */
  disabledReason?: string | null;
  /**
   * The ONLY case that hides this control entirely: the WebContainer publish path isn't
   * active for this conversation at all (e.g. the browser lacks cross-origin isolation, so
   * the legacy panel — which can't deploy anything — is showing instead). Defaults false.
   */
  unavailable?: boolean;
  /** Preview iframe is interactive (see PrototyperPanel docs). Defaults true. */
  previewReady?: boolean;
  isLoading?: boolean;
}

/**
 * The "Publish on Ideoz.ai" dropdown, extracted from `PrototyperPanel` so it can
 * render either in that panel's own header (legacy layout) or inside the merged
 * `Workbench` toolbar (via the header slot). Owns its own slug + publishing state.
 * Hidden only when the WebContainer publish path itself is `unavailable`; otherwise
 * always rendered, disabled with `disabledReason` while not yet publish-ready.
 */
export function PrototyperPublishMenu({
  onPublish,
  prototyperDemoLinkId,
  prototyperSlug,
  userName,
  platformLink = 'https://ideoz.ai/',
  publishReady,
  disabledReason,
  unavailable = false,
  previewReady = true,
  isLoading = false,
}: PrototyperPublishMenuProps): JSX.Element | null {
  const [generatedSlug, setGeneratedSlug] = useState<string>('');
  const [isPublishing, setIsPublishing] = useState(false);
  const { trackEvent } = useTrackEvent();

  useEffect(() => {
    if (prototyperSlug) {
      setGeneratedSlug(prototyperSlug);
    } else if (userName) {
      // `prototyperDemoLinkId` only arrives via the live `demoLinkCreated` SSE event, so a
      // resumed/restored build never has it — fall back to a random id so the suggested slug
      // still generates (publish itself doesn't require this id, only a unique-looking slug).
      const idPart = prototyperDemoLinkId ?? Math.floor(1000 + Math.random() * 9000);
      const randomDigits = Math.floor(1000 + Math.random() * 9000);
      const cleanUserName = userName.toLowerCase().replace(/\s+/g, '');
      setGeneratedSlug(`${cleanUserName}/${idPart}${randomDigits}`);
    }
  }, [prototyperSlug, userName, prototyperDemoLinkId]);

  // `isLoading`/`previewReady` are the cold-start signals (WebContainer still booting /
  // preview not interactive yet) — before either resolves there's nothing meaningful to
  // publish, so the button stays disabled with a generic reason rather than the caller's
  // more specific `disabledReason` (which assumes the workbench is already up).
  const effectiveDisabledReason = !previewReady || isLoading ? 'Preparing prototype…' : disabledReason;
  const isPublishDisabled = Boolean(effectiveDisabledReason);
  const showPublishReadyDot = !isPublishDisabled && Boolean(publishReady);
  const fullPrototypeUrl = `${platformLink}p/${generatedSlug}`;

  const handlePublish = async () => {
    if (!onPublish) return;
    setIsPublishing(true);
    try {
      if (prototyperSlug) {
        await onPublish(null);
        toastSuccess('Prototype unpublished successfully');
      } else {
        if (!generatedSlug) return;
        await onPublish(generatedSlug);
        trackEvent('prototype_shared', { slug: generatedSlug });
        showToast({
          message: 'Prototype published successfully on',
          link: fullPrototypeUrl,
          linkText: fullPrototypeUrl,
          severity: 'success',
        });
      }
    } catch {
      /* parent shows error toast */
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(fullPrototypeUrl);
    toastSuccess('Link copied to clipboard');
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
  };

  if (unavailable) return null;

  // Not yet published and not ready: show a disabled trigger with the reason as its
  // tooltip, and skip the dropdown entirely (there'd be nothing actionable inside it).
  if (!prototyperSlug && isPublishDisabled) {
    return (
      <Button variant="primary" size="sm" disabled title={effectiveDisabledReason ?? undefined}>
        Publish
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="relative">
          <Button variant="primary" size="sm">
            Publish
          </Button>
          {showPublishReadyDot && (
            <span className="absolute -top-1 right-2 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#17B26A] border border-[#FAFAFA]"></span>
            </span>
          )}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="bottom"
        align="end"
        className="w-[400px] p-5 text-sm bg-primary-900 rounded-xl"
      >
        <div className="mb-4">
          <div className="flex items-start gap-3 text-white mb-3">
            <Icon icon={Globe} color="#fff" className="mt-0.5 flex-shrink-0" size={24} />
            <div>
              <h3 className="font-semibold mb-1">Publish on Ideoz.ai</h3>
              <p className="text-[#D5D7DA] text-xs">
                Share your prototype with others by publishing to the web.
              </p>
            </div>
          </div>
        </div>

        <DropdownMenuSeparator className="bg-[#414651] my-3" />

        <div className="mb-4">
          <div className="flex items-start gap-2">
            <Icon icon={FileText} color="#D5D7DA" size={22} className="mt-0.5 flex-shrink-0" />
            <div className="flex gap-8 items-start">
              <span className="text-[#D5D7DA] text-xs leading-6">URL</span>
              <span className="text-white text-xs leading-6 break-all">
                {fullPrototypeUrl.replace(/^(https?:\/\/)/, '')}
              </span>
            </div>
          </div>
        </div>

        <DropdownMenuSeparator className="bg-[#414651] my-3" />

        {prototyperSlug ? (
          <div className="space-y-2">
            <Button
              onClick={handleCopyLink}
              size="sm"
              className="w-full bg-white text-primary-900 font-semibold py-2.5 px-4 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Copy link
            </Button>
            <Button
              onClick={handlePublish}
              disabled={isPublishing}
              size="sm"
              variant="ghost"
              className="w-full text-white font-semibold py-2.5 px-4 rounded-lg hover:bg-[#414651] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPublishing ? (
                <>
                  <Icon icon={Loader2} className="animate-spin mr-2" size={16} />
                  Unpublishing...
                </>
              ) : (
                'Unpublish'
              )}
            </Button>
          </div>
        ) : (
          <Button
            onClick={handlePublish}
            disabled={isPublishing || !generatedSlug}
            size="sm"
            className="w-full bg-white text-primary-900 font-semibold py-2.5 px-4 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPublishing ? (
              <>
                <Icon icon={Loader2} className="animate-spin mr-2" size={16} />
                Publishing...
              </>
            ) : (
              'Publish'
            )}
          </Button>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default PrototyperPublishMenu;
